import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // Create link modal
  createLinkModalOpen: boolean;
  openCreateLinkModal: () => void;
  closeCreateLinkModal: () => void;

  // Import links modal
  importLinksModalOpen: boolean;
  openImportLinksModal: () => void;
  closeImportLinksModal: () => void;

  // Share modal
  shareModalLink: {
    id: string;
    shortCode: string;
    title: string | null;
    contextType?: 'link' | 'project';
  } | null;
  openShareModal: (context: {
    id: string;
    shortCode: string;
    title: string | null;
    contextType?: 'link' | 'project';
  }) => void;
  closeShareModal: () => void;

  // QR code modal
  qrModalLink: { shortCode: string; url: string } | null;
  openQrModal: (shortCode: string, url: string) => void;
  closeQrModal: () => void;

  // Delete confirmation
  deleteConfirmation: {
    type: 'link' | 'token' | 'user' | 'qr-code' | 'page' | 'form';
    id: string;
    name: string;
  } | null;
  openDeleteConfirmation: (
    type: 'link' | 'token' | 'user' | 'qr-code' | 'page' | 'form',
    id: string,
    name: string
  ) => void;
  closeDeleteConfirmation: () => void;

  // URL change confirmation
  urlChangeConfirmation: { shortCode: string; currentUrl: string; newUrl: string } | null;
  openUrlChangeConfirmation: (shortCode: string, currentUrl: string, newUrl: string) => void;
  closeUrlChangeConfirmation: () => void;

  /**
   * @deprecated Use route /pages/new or /pages/:id/edit instead of PageEditorModal.
   * These properties are kept for backwards compatibility with the modal component.
   */
  pageEditorPageId: string | null | undefined;
  /** @deprecated Use navigate('/pages/new') or navigate(`/pages/${id}/edit`) instead */
  openPageEditor: (pageId?: string) => void;
  /** @deprecated Modal is no longer used - navigation handles closing */
  closePageEditor: () => void;

  /**
   * @deprecated Template picker is no longer used. Navigate directly to /pages/new?template=<type>
   */
  templatePickerOpen: boolean;
  /** @deprecated Use URL query param ?template=<type> instead */
  selectedTemplateType: string | null;
  /** @deprecated Navigate to /pages/new instead */
  openTemplatePicker: () => void;
  /** @deprecated Modal is no longer used */
  closeTemplatePicker: () => void;
  /** @deprecated Navigate to /pages/new?template=<type> instead */
  selectTemplateAndOpenEditor: (templateType: string) => void;

  /**
   * @deprecated Use route /forms/new or /forms/:id/edit instead of FormBuilderModal.
   * These properties are kept for backwards compatibility with the modal component.
   */
  formBuilderFormId: string | null | undefined;
  /** @deprecated Use navigate('/forms/new') or navigate(`/forms/${id}/edit`) instead */
  openFormBuilder: (formId?: string) => void;
  /** @deprecated Modal is no longer used - navigation handles closing */
  closeFormBuilder: () => void;

  // Submission detail modal
  submissionDetailId: { formId: string; submissionId: string } | null;
  openSubmissionDetail: (formId: string, submissionId: string) => void;
  closeSubmissionDetail: () => void;
}

/**
 * Determine if dark mode should be applied based on theme setting
 */
function shouldUseDarkMode(theme: Theme): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  // System preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Apply theme to document
 */
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (shouldUseDarkMode(theme)) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Initialize theme on load (before React hydrates)
 *
 * Note: Public pages (landing, about, pricing) force light mode via useForceLightMode hook.
 * This initialization only affects the dashboard where users can toggle themes.
 */
function initializeTheme() {
  try {
    const stored = localStorage.getItem('arml-ui-store');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Default to 'light' if no theme stored (was 'system')
      const theme = parsed?.state?.theme || 'light';
      applyTheme(theme);
    } else {
      // Default to light mode for new users
      applyTheme('light');
    }
  } catch {
    // Default to light mode on error
    applyTheme('light');
  }
}

// Run immediately (for dashboard pages)
if (typeof window !== 'undefined') {
  initializeTheme();

  // Listen for system theme changes when in 'system' mode
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    try {
      const stored = localStorage.getItem('arml-ui-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.state?.theme === 'system') {
          applyTheme('system');
        }
      }
    } catch {
      // Ignore errors
    }
  });
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Theme - default to light (users can enable dark mode in dashboard settings)
      theme: 'light',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },

      // Create link modal
      createLinkModalOpen: false,
      openCreateLinkModal: () => set({ createLinkModalOpen: true }),
      closeCreateLinkModal: () => set({ createLinkModalOpen: false }),

      // Import links modal
      importLinksModalOpen: false,
      openImportLinksModal: () => set({ importLinksModalOpen: true }),
      closeImportLinksModal: () => set({ importLinksModalOpen: false }),

      // Share modal
      shareModalLink: null,
      openShareModal: (context) => set({ shareModalLink: context }),
      closeShareModal: () => set({ shareModalLink: null }),

      // QR code modal
      qrModalLink: null,
      openQrModal: (shortCode, url) => set({ qrModalLink: { shortCode, url } }),
      closeQrModal: () => set({ qrModalLink: null }),

      // Delete confirmation
      deleteConfirmation: null,
      openDeleteConfirmation: (type, id, name) => set({ deleteConfirmation: { type, id, name } }),
      closeDeleteConfirmation: () => set({ deleteConfirmation: null }),

      // URL change confirmation
      urlChangeConfirmation: null,
      openUrlChangeConfirmation: (shortCode, currentUrl, newUrl) =>
        set({ urlChangeConfirmation: { shortCode, currentUrl, newUrl } }),
      closeUrlChangeConfirmation: () => set({ urlChangeConfirmation: null }),

      // Page editor modal (DEPRECATED - use routes instead)
      pageEditorPageId: null,
      openPageEditor: (pageId) => set({ pageEditorPageId: pageId ?? undefined }),
      closePageEditor: () => set({ pageEditorPageId: null, selectedTemplateType: null }),

      // Template picker modal (DEPRECATED - use routes instead)
      templatePickerOpen: false,
      selectedTemplateType: null,
      openTemplatePicker: () => set({ templatePickerOpen: true }),
      closeTemplatePicker: () => set({ templatePickerOpen: false }),
      selectTemplateAndOpenEditor: (templateType) =>
        set({
          templatePickerOpen: false,
          selectedTemplateType: templateType,
          pageEditorPageId: undefined, // undefined = create mode
        }),

      // Form builder modal (DEPRECATED - use routes instead)
      formBuilderFormId: null,
      openFormBuilder: (formId) => set({ formBuilderFormId: formId ?? undefined }),
      closeFormBuilder: () => set({ formBuilderFormId: null }),

      // Submission detail modal
      submissionDetailId: null,
      openSubmissionDetail: (formId, submissionId) =>
        set({ submissionDetailId: { formId, submissionId } }),
      closeSubmissionDetail: () => set({ submissionDetailId: null }),
    }),
    {
      name: 'arml-ui-store',
      partialize: (state) => ({ theme: state.theme }), // Only persist theme
      onRehydrateStorage: () => (state) => {
        // Apply theme after rehydration
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

/**
 * Get the resolved theme (for components that need to know the actual dark/light state)
 */
export function getResolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return theme;
}
